import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

import { API } from "../lib/api";

const AuthContext = createContext(null);

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not authed, obj = authed
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {}
  }, []);

  const login = async (email, password) => {
    try {
      await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || "Invalid email or password" };
    }
    try {
      const { data: fullUser } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(fullUser);
      return { success: true };
    } catch {
      return { success: false, error: "Login succeeded but session failed. Please try again." };
    }
  };

  const loginWithGoogle = async (sessionId) => {
    try {
      await axios.post(`${API}/api/auth/google`, { session_id: sessionId }, { withCredentials: true });
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || "Google sign-in failed" };
    }
    try {
      const { data: fullUser } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(fullUser);
      return { success: true };
    } catch {
      return { success: false, error: "Google sign-in succeeded but session failed. Please try again." };
    }
  };

  const register = async (name, email, password) => {
    try {
      await axios.post(`${API}/api/auth/register`, { name, email, password }, { withCredentials: true });
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || "Registration failed" };
    }
    try {
      const { data: fullUser } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(fullUser);
      return { success: true };
    } catch {
      return { success: false, error: "Account created! Please sign in." };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
